import { supabase } from '../supabase/client';
import type { StaffUser, StaffUserInsert } from '@/types/staff';
import { logActivity } from './activityService';

export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  data?: any;
  error?: string;
}

/**
 * Staff registration (self-service, requires admin approval)
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  try {
    console.log('[AuthService] Starting registration for email:', data.email);
    
    // Step 1: Create Supabase Auth user
    console.log('[AuthService] Step 1: Creating Supabase Auth user...');
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
        },
      },
    });

    if (authError) {
      console.error('[AuthService] Supabase Auth registration FAILED:', {
        error: authError,
        code: authError.status,
        message: authError.message,
        email: data.email,
      });
      return { success: false, error: authError.message };
    }

    if (!authData.user) {
      console.error('[AuthService] No user returned from signUp');
      return { success: false, error: 'Failed to create user account' };
    }

    console.log('[AuthService] Supabase Auth user created successfully:', {
      user_id: authData.user.id,
      email: authData.user.email,
    });

    // Step 2: Create staff_users record (pending approval)
    const staffUserData: StaffUserInsert = {
      auth_user_id: authData.user.id,
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number || null,
      role: 'cs_rep', // Default role
      status: 'pending_approval',
      // Default permissions for CS Rep
      can_view_customers: true,
      can_edit_customers: false,
      can_create_cases: true,
      can_close_cases: false,
      can_send_sms: false,
      can_view_telemetry: false,
      can_manage_staff: false,
      can_configure_system: false,
    };

    console.log('[AuthService] Creating staff_users record with data:', {
      auth_user_id: staffUserData.auth_user_id,
      email: staffUserData.email,
      role: staffUserData.role,
      status: staffUserData.status,
    });

    const { data: staffRecord, error: staffError } = await supabase
      .from('staff_users')
      .insert(staffUserData as any)
      .select()
      .single() as any;

    if (staffError) {
      console.error('[AuthService] Staff user creation FAILED:', {
        error: staffError,
        code: staffError.code,
        message: staffError.message,
        details: staffError.details,
        hint: staffError.hint,
        auth_user_id: authData.user.id,
        email: data.email,
      });
      
      // Note: Cannot cleanup auth user from client side (requires service role)
      // The auth user will remain but without a staff_users record
      console.warn('[AuthService] Auth user created but staff profile failed - manual cleanup may be needed');
      console.error('[AuthService] To cleanup, delete user from Supabase Dashboard: Users → Delete user with email:', data.email);
      
      return { 
        success: false, 
        error: `Failed to create staff profile: ${staffError.message} (Code: ${staffError.code}). Please contact administrator.` 
      };
    }

    console.log('[AuthService] Staff user created successfully:', {
      staff_id: (staffRecord as any)?.id,
      email: (staffRecord as any)?.email,
      status: (staffRecord as any)?.status,
    });

    console.log('[AuthService] Registration successful, pending approval');
    return {
      success: true,
      data: {
        user: authData.user,
        status: 'pending_approval',
        message: 'Registration successful. Awaiting admin approval.',
      },
    };
  } catch (error: any) {
    console.error('[AuthService] Unexpected registration error:', error);
    return { success: false, error: error.message || 'Registration failed' };
  }
};

/**
 * Staff login
 */
export const login = async (data: LoginData): Promise<AuthResponse> => {
  try {
    // Step 1: Sign in with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (authError) {
      console.error('[AuthService] Login error:', authError);
      return { success: false, error: authError.message };
    }

    if (!authData.user) {
      return { success: false, error: 'Login failed' };
    }

    // Step 2: Get staff_users record
    const { data: staffUser, error: staffError } = await supabase
      .from('staff_users')
      .select('*')
      .eq('auth_user_id', authData.user.id)
      .single() as any;

    if (staffError || !staffUser) {
      console.error('[AuthService] Staff profile not found:', staffError);
      await supabase.auth.signOut();
      return { success: false, error: 'Staff profile not found' };
    }

    // Step 3: Check if staff is approved
    if (staffUser.status !== 'active') {
      await supabase.auth.signOut();
      return {
        success: false,
        error: `Account status: ${staffUser.status}. Please contact an administrator.`,
      };
    }

    // Step 4: Update last login time
    await ((supabase
      .from('staff_users') as any)
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', staffUser.id));

    // Step 5: Log activity
    await logActivity({
      staff_user_id: staffUser.id,
      action_type: 'login',
      entity_type: 'staff_user',
      entity_id: staffUser.id,
      details: { email: data.email },
    });

    console.log('[AuthService] Login successful:', staffUser.email);
    return {
      success: true,
      data: {
        session: authData.session,
        user: authData.user,
        staffUser,
      },
    };
  } catch (error: any) {
    console.error('[AuthService] Unexpected login error:', error);
    return { success: false, error: error.message || 'Login failed' };
  }
};

/**
 * Logout
 */
export const logout = async (): Promise<AuthResponse> => {
  try {
    // Get current staff user before logout
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      const { data: staffUser } = await supabase
        .from('staff_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single() as any;

      if (staffUser) {
        await logActivity({
          staff_user_id: (staffUser as any).id,
          action_type: 'logout',
          entity_type: 'staff_user',
          entity_id: staffUser.id,
        });
      }
    }

    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('[AuthService] Logout error:', error);
      return { success: false, error: error.message };
    }

    console.log('[AuthService] Logout successful');
    return { success: true };
  } catch (error: any) {
    console.error('[AuthService] Unexpected logout error:', error);
    return { success: false, error: error.message || 'Logout failed' };
  }
};

/**
 * Get current session
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('[AuthService] Get session error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data.session };
  } catch (error: any) {
    console.error('[AuthService] Unexpected session error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get current staff user profile
 */
export const getProfile = async (): Promise<AuthResponse> => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: staffUser, error: staffError } = await supabase
      .from('staff_users')
      .select('*')
      .eq('auth_user_id', user.id)
      .single() as any;

    if (staffError || !staffUser) {
      console.error('[AuthService] Staff profile not found:', staffError);
      return { success: false, error: 'Staff profile not found' };
    }

    return { success: true, data: staffUser };
  } catch (error: any) {
    console.error('[AuthService] Unexpected profile error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update staff profile
 */
export const updateProfile = async (
  updates: Partial<Pick<StaffUser, 'first_name' | 'last_name' | 'phone_number'>>
): Promise<AuthResponse> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await ((supabase
      .from('staff_users') as any)
      .update(updates)
      .eq('auth_user_id', user.id)
      .select()
      .single());

    if (error) {
      console.error('[AuthService] Profile update error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: (data as any).id,
      action_type: 'update_profile',
      entity_type: 'staff_user',
      entity_id: data.id,
      details: { updates },
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[AuthService] Unexpected update error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Change password
 */
export const changePassword = async (newPassword: string): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error('[AuthService] Password change error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[AuthService] Unexpected password change error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (email: string): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('[AuthService] Password reset request error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: { message: 'Password reset email sent' } };
  } catch (error: any) {
    console.error('[AuthService] Unexpected password reset error:', error);
    return { success: false, error: error.message };
  }
};

export const authService = {
  register,
  login,
  logout,
  getSession,
  getProfile,
  updateProfile,
  changePassword,
  requestPasswordReset,
};

